import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Calendar, Clock, MapPin, Award, Building2, LayoutGrid, Globe, BarChart3, ChevronDown, CheckCircle2, Landmark, Shield, Database, Users, Scale, TrendingUp, Linkedin, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FallingMapleLeaves } from '@/components/shared/FallingMapleLeaves';
import { TradingCycleDiagram } from '@/components/ccts/TradingCycleDiagram';
import { EntityShareBar } from '@/components/ccts/EntityShareBar';
import { ComplianceCycleSection } from '@/components/ccts/ComplianceCycleSection';
import { CCTS_STATS, WHAT_CCTS_IS, INSTITUTIONS, TIMELINE_ITEMS, CALLOUT_TEXT, FIVE_TAKEAWAYS, SECTORS, DAY1_MODULES, DAY2_MODULES, OUTCOMES, SPEAKERS, FAQ_ITEMS } from '@/data/cctsData';

const statIcons = [Building2, LayoutGrid, Globe, BarChart3];
const instIcons = [Landmark, Globe, Database, Scale, Shield, Users];

function RazorpayButton() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || ref.current.querySelector('script')) return;
    const form = document.createElement('form');
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/payment-button.js';
    script.setAttribute('data-payment_button_id', 'pl_TIE0WJUatku6eS');
    script.async = true;
    form.appendChild(script);
    ref.current.appendChild(form);
  }, []);
  return <div ref={ref} data-testid="razorpay-button" />;
}

function Countdown() {
  const [t, setT] = useState({});
  useEffect(() => {
    const target = new Date('2026-09-01T00:00:00+05:30');
    const tick = () => { const d = target - new Date(); if (d <= 0) return setT({ d:0,h:0,m:0,s:0 }); setT({ d: Math.floor(d/86400000), h: Math.floor((d%86400000)/3600000), m: Math.floor((d%3600000)/60000), s: Math.floor((d%60000)/1000) }); };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, []);
  return (
    <div className="flex gap-3">
      {[['d','Days'],['h','Hrs'],['m','Min'],['s','Sec']].map(([k,l]) => (
        <div key={k} className="text-center">
          <div className="w-14 h-14 bg-brand-green/10 border border-brand-green/20 rounded-xl flex items-center justify-center font-mono text-xl font-bold text-brand-green">{String(t[k]||0).padStart(2,'0')}</div>
          <span className="font-mono text-[10px] text-gray-400 uppercase mt-1 block">{l}</span>
        </div>
      ))}
    </div>
  );
}

function Accordion({ title, children, badge, time, accent='green' }) {
  const [open, setOpen] = useState(false);
  const c = accent==='green' ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-brown/10 text-brand-brown';
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 hover:bg-brand-surface/50 transition-colors text-left">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className={`font-mono text-xs px-3 py-1 rounded-full flex-shrink-0 ${c}`}>{title.split(' ')[0]}</span>
          <span className="font-sans text-base text-brand-dark font-bold">{title.split(' ').slice(1).join(' ')}</span>
          {badge && <span className="font-mono text-[10px] bg-brand-green text-white px-2 py-0.5 rounded-full uppercase flex-shrink-0">{badge}</span>}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
          <span className="font-mono text-sm text-gray-400">{time}</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      <AnimatePresence>{open && (
        <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} transition={{duration:0.2}}>
          <div className="px-4 pb-4">{children}</div>
        </motion.div>
      )}</AnimatePresence>
    </div>
  );
}

const CCTSTrainingPage = () => {
  const [currTab, setCurrTab] = useState('day1');
  const [mechTab, setMechTab] = useState('compliance');
  const [heroSlide, setHeroSlide] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setHeroSlide(p => (p+1)%2), 5000);
    return () => clearInterval(id);
  }, []);

  const slides = [
    { eyebrow: 'CCTS MASTERY', title: 'India Just Built Its First Mandatory Carbon Market.', sub: "Here's What That Means for You." },
    { eyebrow: 'CERTIFICATE PROGRAMME', title: 'Navigating India\'s Carbon Credit Trading Scheme.', sub: 'A compliance-to-market training for obligated entities and carbon market professionals.' },
  ];

  return (
    <main data-testid="ccts-training-page" className="overflow-hidden bg-white">
      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative pt-24 pb-12 overflow-hidden">
        <div className="absolute inset-0 -top-20">
          <img src="https://images.unsplash.com/photo-1601655856683-8400aa9ef59f?w=1920&q=80" alt="" className="w-full h-full object-cover" style={{ filter: 'saturate(0.6) brightness(1.1) contrast(0.95) sepia(0.15)' }} />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/60" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white" />
        </div>
        <FallingMapleLeaves />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
          <Link to="/events" className="inline-flex items-center font-sans text-sm text-gray-500 hover:text-brand-green transition-colors mb-4"><ArrowLeft className="w-4 h-4 mr-1.5" />Events</Link>
          <div className="relative min-h-[180px] mb-5">
            <AnimatePresence mode="wait">
              <motion.div key={heroSlide} initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-20}} transition={{duration:0.4}}>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="font-mono text-xs text-brand-green uppercase tracking-[0.15em] bg-brand-green/10 px-3 py-1 rounded-full">{slides[heroSlide].eyebrow}</span>
                  <span className="font-mono text-xs text-gray-500 uppercase tracking-[0.1em] bg-gray-100 px-3 py-1 rounded-full">Live Training</span>
                </div>
                <h1 className="font-serif text-4xl sm:text-5xl text-brand-dark mb-3 leading-[1.1] max-w-2xl">{slides[heroSlide].title}</h1>
                <p className="font-sans text-lg text-gray-600 max-w-lg">{slides[heroSlide].sub}</p>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="flex gap-2 mb-5">
            {slides.map((_, idx) => (
              <button key={`slide-${idx}`} onClick={() => setHeroSlide(idx)} className={`w-2.5 h-2.5 rounded-full transition-all ${heroSlide===idx ? 'bg-brand-green w-6' : 'bg-gray-300'}`} />
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            {[['12\u201313 Sep 2026',Calendar],['2 Days, 3 hrs each',Clock],['Live on Zoom',MapPin],['Certificate',Award]].map(([t,I]) => (
              <span key={t} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/70 border border-gray-200 text-sm text-gray-600"><I className="w-4 h-4" />{t}</span>
            ))}
          </div>
          <div className="flex items-center gap-4 mb-5">
            <span className="font-serif text-3xl text-brand-dark font-bold">₹2,999</span>
            <span className="font-sans text-base text-gray-400 line-through">₹4,999</span>
            <span className="font-mono text-xs bg-brand-green text-white px-2.5 py-1 rounded-full uppercase">Save 40%</span>
          </div>
          <RazorpayButton />
        </div>
      </section>

      {/* ═══════════════ WHAT CCTS IS — BENTO GRID ═══════════════ */}
      <section className="py-14 relative overflow-hidden bg-gradient-to-br from-brand-green/[0.03] via-transparent to-brand-brown/[0.03]" data-testid="what-ccts-bento">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {/* Section header */}
          <div className="mb-6">
            <h2 className="font-serif text-4xl lg:text-5xl text-brand-dark tracking-tight mb-3" data-testid="what-ccts-heading">What CCTS <span className="bg-gradient-to-r from-brand-brown to-brand-green bg-clip-text text-transparent">Is</span></h2>
            <p className="font-sans text-lg text-gray-500 max-w-2xl">India&apos;s first mandatory carbon market, from scheme design to your compliance playbook.</p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* LEFT: 6 stacked cards */}
            <div className="lg:col-span-4 flex flex-col gap-3">
              {WHAT_CCTS_IS.map((p,i) => (
                <motion.div
                  key={`ccts-bullet-${i}`}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="bg-white rounded-2xl p-5 border border-gray-200/60 shadow-sm flex items-start gap-4 hover:shadow-md hover:border-brand-green/20 transition-shadow"
                  data-testid={`ccts-bullet-card-${i}`}
                >
                  <div className="w-8 h-8 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle2 className="w-4 h-4 text-brand-green" />
                  </div>
                  <p className="font-sans text-sm text-gray-600 leading-relaxed">
                    {p.bold ? <><strong className="text-brand-dark">{p.bold}</strong>{p.text}</> : p.text}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* RIGHT: Diagram + Stats */}
            <div className="lg:col-span-8 flex flex-col gap-5">
              {/* Diagram card */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-[2rem] p-8 md:p-10 border border-gray-200/60 shadow-sm relative overflow-hidden flex-1"
              >
                {/* Subtle glow */}
                <div className="absolute -top-20 -right-20 w-60 h-60 bg-brand-green/5 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-brand-brown/5 rounded-full blur-3xl pointer-events-none" />
                <div className="relative z-10">
                  <h3 className="font-serif text-2xl md:text-3xl text-brand-dark tracking-tight mb-1">The Trading <span className="bg-gradient-to-r from-brand-green to-brand-brown bg-clip-text text-transparent">Cycle</span></h3>
                  <p className="font-sans text-sm text-gray-400 mb-6">Complete flow from measurement to banking</p>
                  <div className="bg-gray-50/40 rounded-2xl p-4 md:p-6 border border-gray-100/60">
                    <TradingCycleDiagram />
                  </div>
                </div>
              </motion.div>

              {/* Two stat cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="bg-brand-brown/5 rounded-[1.5rem] p-7 border border-brand-brown/10"
                >
                  <Globe className="w-6 h-6 text-brand-brown/60 mb-3" />
                  <p className="font-serif text-3xl text-brand-dark font-bold tracking-tight">3rd Largest</p>
                  <p className="font-sans text-sm text-gray-500 mt-1">On track to be the world&apos;s 3rd-largest ETS by coverage.</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="bg-brand-green/5 rounded-[1.5rem] p-7 border border-brand-green/10"
                >
                  <TrendingUp className="w-6 h-6 text-brand-green/60 mb-3" />
                  <p className="font-serif text-3xl text-brand-dark font-bold tracking-tight">~180 MtCO₂e</p>
                  <p className="font-sans text-sm text-gray-500 mt-1">Projected CCC demand by 2030, ~₹45,000 crore/year.</p>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ BIG STATS ═══════════════ */}
      <section className="py-10 relative overflow-hidden" data-testid="ccts-stats-section">
        {/* Canvas background matching hero */}
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1601655856683-8400aa9ef59f?w=1920&q=80" alt="" className="w-full h-full object-cover" style={{ filter: 'saturate(0.6) brightness(1.1) contrast(0.95) sepia(0.15)' }} />
          <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/70 to-white/60" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-white/60" />
        </div>
        <FallingMapleLeaves />
        {/* Decorative maple leaves */}
        <svg viewBox="0 0 100 100" className="absolute top-4 left-[8%] w-16 h-16 text-brand-green/[0.07] rotate-[-20deg]" fill="currentColor"><path d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/></svg>
        <svg viewBox="0 0 100 100" className="absolute bottom-6 right-[12%] w-20 h-20 text-brand-brown/[0.06] rotate-[35deg]" fill="currentColor"><path d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/></svg>
        <svg viewBox="0 0 100 100" className="absolute top-1/2 -translate-y-1/2 right-[5%] w-12 h-12 text-brand-green/[0.05] rotate-[60deg]" fill="currentColor"><path d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/></svg>
        <svg viewBox="0 0 100 100" className="absolute bottom-3 left-[25%] w-10 h-10 text-brand-brown/[0.05] rotate-[-45deg]" fill="currentColor"><path d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/></svg>
        <svg viewBox="0 0 100 100" className="absolute top-3 right-[35%] w-14 h-14 text-brand-green/[0.04] rotate-[15deg]" fill="currentColor"><path d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/></svg>
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-3 gap-8">
            {CCTS_STATS.map((s,i) => {
              const Icon = statIcons[i];
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="text-center"
                  data-testid={`ccts-stat-${i}`}
                >
                  <Icon className="w-6 h-6 text-brand-green/50 mx-auto mb-2" />
                  <p className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold leading-none bg-gradient-to-r from-brand-brown via-brand-green to-brand-brown bg-clip-text text-transparent">{s.num}</p>
                  <p className="font-mono text-xs text-gray-500 uppercase tracking-[0.15em] mt-2">{s.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════ WHO RUNS IT (horizontal grid) ═══════════════ */}
      <section className="py-10">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mb-2">Who <span className="bg-gradient-to-r from-brand-brown to-brand-green bg-clip-text text-transparent">Runs It</span></h2>
          <p className="font-sans text-base text-gray-500 mb-4">Six institutions share the job.</p>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {INSTITUTIONS.map((inst,i) => {
              const Icon = instIcons[i];
              return (
                <motion.div key={inst.abbr} whileHover={{y:-2}} className="border border-gray-200 rounded-2xl p-5 bg-white hover:shadow-md hover:border-brand-green/20 transition-all">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-brand-green" />
                    </div>
                    <span className="font-mono text-base font-bold text-brand-dark">{inst.abbr}</span>
                  </div>
                  <p className="font-sans text-xs text-gray-400 mb-1">{inst.name}</p>
                  <p className="font-sans text-sm text-gray-600"><strong className="text-brand-dark">{inst.role}</strong></p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════ LEGISLATIVE TIMELINE (horizontal) ═══════════════ */}
      <section className="py-10 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mb-2">Legislative <span className="bg-gradient-to-r from-brand-green to-brand-brown bg-clip-text text-transparent">Timeline</span></h2>
          <p className="font-sans text-base text-gray-500 mb-6">Key milestones from Act to market launch.</p>
          <div className="relative">
            {/* Horizontal connecting line */}
            <div className="absolute top-[14px] left-0 right-0 h-px bg-gradient-to-r from-brand-brown via-brand-brown/40 to-brand-green hidden md:block" />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {TIMELINE_ITEMS.map((t,i) => (
                <motion.div
                  key={t.date}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="text-center relative"
                >
                  <div className={`w-7 h-7 rounded-full mx-auto mb-3 relative z-10 border-[3px] border-white ${i>=4 ? 'bg-brand-green' : 'bg-brand-brown'}`} />
                  <p className={`font-mono text-sm font-bold ${i>=4 ? 'text-brand-green' : 'text-brand-brown'}`}>{t.date}</p>
                  <p className="font-sans text-sm text-gray-600 mt-1 leading-snug">{t.title}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ CALLOUT + TAKEAWAYS ═══════════════ */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {/* Full-width callout */}
          <div className="bg-brand-brown/5 rounded-2xl p-6 flex items-start gap-4">
            <Clock className="w-6 h-6 text-brand-brown flex-shrink-0 mt-0.5" />
            <p className="font-sans text-base text-gray-700">{CALLOUT_TEXT}</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-5">
            {FIVE_TAKEAWAYS.map((t,i) => (
              <div key={`takeaway-${i}`} className="border border-gray-200 rounded-xl p-4">
                <p className="font-sans text-sm text-gray-600">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ SECTORS (horizontal) ═══════════════ */}
      <section className="py-10 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mb-1">Nine Sectors, Nine Different <span className="bg-gradient-to-r from-brand-brown to-brand-green bg-clip-text text-transparent">Clocks</span></h2>
          <p className="font-sans text-base text-gray-500 mb-6">Here&apos;s exactly where each one stands.</p>

          {/* 5-column grid, 2 rows — all sectors visible */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {SECTORS.map((s,i) => (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className={`rounded-xl p-4 ${s.draft ? 'border-dashed border border-brand-brown/30 bg-brand-brown/5' : 'border border-gray-200 bg-white'}`}
              >
                <p className="font-sans text-sm font-semibold text-brand-dark truncate">{s.name}</p>
                <p className="font-serif text-3xl font-bold text-brand-green mt-1">{s.entities}</p>
                <p className="font-mono text-[10px] text-gray-400">entities</p>
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${s.draft ? 'bg-brand-brown/10 text-brand-brown' : 'bg-brand-green/10 text-brand-green'}`}>{s.status}</span>
                  <p className="font-mono text-[10px] text-gray-400 mt-1">GEI: {s.range}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Entity bar + trajectory below */}
          <div className="mt-6">
            <EntityShareBar />
          </div>
          <div className="mt-4 border border-gray-200 rounded-xl p-5">
            <p className="font-mono text-xs text-gray-400 uppercase tracking-wider">Scheme-Wide Trajectory</p>
            <p className="font-sans text-base text-gray-600 mt-1">FY 2025-26: 1–3% GEI reduction, ratcheting to FY 2026-27: 2–8%, both against the FY 2023-24 baseline.</p>
          </div>
        </div>
      </section>

      {/* ═══════════════ MECHANISMS — EDITORIAL SPLIT ═══════════════ */}
      <section className="py-12 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mb-2">Two Mechanisms, Two Kinds of <span className="bg-gradient-to-r from-brand-green to-brand-brown bg-clip-text text-transparent">Entities</span></h2>
          <p className="font-sans text-base text-gray-500 mb-6">Know which one applies to you.</p>

          <div className="flex flex-col md:flex-row gap-4">
            {/* Compliance card */}
            <motion.div
              layout
              onClick={() => setMechTab('compliance')}
              tabIndex={0}
              role="button"
              aria-expanded={mechTab==='compliance'}
              data-testid="mechanism-card-compliance"
              className={`rounded-3xl cursor-pointer overflow-hidden relative transition-shadow duration-300 ${
                mechTab==='compliance'
                  ? 'flex-[1.6] bg-[#a6533a] text-white shadow-[0_8px_40px_rgba(166,83,58,0.2)]'
                  : 'flex-1 bg-white border border-[rgba(166,83,58,0.15)] hover:shadow-lg hover:scale-[1.01]'
              }`}
              transition={{ type: 'spring', stiffness: 200, damping: 25 }}
            >
              {/* Grain overlay on active */}
              {mechTab==='compliance' && <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")'}} />}
              <div className="relative z-10 p-8 md:p-10">
                <span className={`font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] px-3 py-1 rounded-full inline-block mb-5 ${mechTab==='compliance' ? 'bg-white/20 text-white' : 'bg-brand-brown/10 text-brand-brown'}`}>Mandatory</span>
                <h3 className={`font-serif text-2xl md:text-3xl mb-6 ${mechTab==='compliance' ? 'text-white' : 'text-brand-dark'}`}><span className="italic">Compliance</span> Mechanism</h3>

                <div className="space-y-5">
                  <div className={`rounded-2xl p-4 ${mechTab==='compliance' ? 'bg-white/10' : 'bg-brand-brown/5'}`}>
                    <p className={`font-mono text-[10px] uppercase tracking-[0.15em] mb-2 ${mechTab==='compliance' ? 'text-white/60' : 'text-gray-400'}`}>Who</p>
                    <p className={`font-sans text-sm leading-relaxed ${mechTab==='compliance' ? 'text-white/90' : 'text-gray-600'}`}>Industrial entities in 9 notified sectors exceeding energy-consumption thresholds</p>
                  </div>
                  <div className={`rounded-2xl p-4 ${mechTab==='compliance' ? 'bg-white/10' : 'bg-brand-brown/5'}`}>
                    <p className={`font-mono text-[10px] uppercase tracking-[0.15em] mb-2 ${mechTab==='compliance' ? 'text-white/60' : 'text-gray-400'}`}>Must Do</p>
                    <p className={`font-sans text-sm leading-relaxed ${mechTab==='compliance' ? 'text-white/90' : 'text-gray-600'}`}>Hit assigned emission-intensity targets, surrender CCCs for any shortfall</p>
                  </div>
                  <div className="flex gap-3">
                    <div className={`flex-1 rounded-xl p-3 ${mechTab==='compliance' ? 'bg-white/10' : 'bg-brand-green/5 border border-brand-green/10'}`}>
                      <p className={`font-sans text-xs font-semibold mb-1 ${mechTab==='compliance' ? 'text-white' : 'text-brand-green'}`}>Beat target</p>
                      <p className={`font-sans text-xs ${mechTab==='compliance' ? 'text-white/70' : 'text-gray-500'}`}>BEE issues CCCs to you</p>
                    </div>
                    <div className={`flex-1 rounded-xl p-3 ${mechTab==='compliance' ? 'bg-white/10' : 'bg-brand-brown/5 border border-brand-brown/10'}`}>
                      <p className={`font-sans text-xs font-semibold mb-1 ${mechTab==='compliance' ? 'text-white' : 'text-brand-brown'}`}>Miss target</p>
                      <p className={`font-sans text-xs ${mechTab==='compliance' ? 'text-white/70' : 'text-gray-500'}`}>Buy CCCs or pay compensation</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Offset card */}
            <motion.div
              layout
              onClick={() => setMechTab('offset')}
              tabIndex={0}
              role="button"
              aria-expanded={mechTab==='offset'}
              data-testid="mechanism-card-offset"
              className={`rounded-3xl cursor-pointer overflow-hidden relative transition-shadow duration-300 ${
                mechTab==='offset'
                  ? 'flex-[1.6] bg-[#1a5c1e] text-white shadow-[0_8px_40px_rgba(26,92,30,0.2)]'
                  : 'flex-1 bg-white border border-[rgba(26,92,30,0.15)] hover:shadow-lg hover:scale-[1.01]'
              }`}
              transition={{ type: 'spring', stiffness: 200, damping: 25 }}
            >
              {mechTab==='offset' && <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")'}} />}
              <div className="relative z-10 p-8 md:p-10">
                <span className={`font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] px-3 py-1 rounded-full inline-block mb-5 ${mechTab==='offset' ? 'bg-white/20 text-white' : 'bg-brand-green/10 text-brand-green'}`}>Voluntary</span>
                <h3 className={`font-serif text-2xl md:text-3xl mb-6 ${mechTab==='offset' ? 'text-white' : 'text-brand-dark'}`}><span className="italic">Offset</span> Mechanism</h3>

                <div className="space-y-5">
                  <div className={`rounded-2xl p-4 ${mechTab==='offset' ? 'bg-white/10' : 'bg-brand-green/5'}`}>
                    <p className={`font-mono text-[10px] uppercase tracking-[0.15em] mb-2 ${mechTab==='offset' ? 'text-white/60' : 'text-gray-400'}`}>Who</p>
                    <p className={`font-sans text-sm leading-relaxed ${mechTab==='offset' ? 'text-white/90' : 'text-gray-600'}`}>Entities outside the 9 sectors: forestry, agriculture, mangroves, clean cooking</p>
                  </div>
                  <div className={`rounded-2xl p-4 ${mechTab==='offset' ? 'bg-white/10' : 'bg-brand-green/5'}`}>
                    <p className={`font-mono text-[10px] uppercase tracking-[0.15em] mb-2 ${mechTab==='offset' ? 'text-white/60' : 'text-gray-400'}`}>Rulebook</p>
                    <p className={`font-sans text-sm leading-relaxed ${mechTab==='offset' ? 'text-white/90' : 'text-gray-600'}`}>BEE&apos;s Offset Procedure under CCTS, Mar 2025. 12 methodologies as of Jul 2026.</p>
                  </div>
                  <div className={`rounded-2xl p-4 ${mechTab==='offset' ? 'bg-white/10' : 'bg-brand-green/5'}`}>
                    <p className={`font-mono text-[10px] uppercase tracking-[0.15em] mb-2 ${mechTab==='offset' ? 'text-white/60' : 'text-gray-400'}`}>Do</p>
                    <p className={`font-sans text-sm leading-relaxed ${mechTab==='offset' ? 'text-white/90' : 'text-gray-600'}`}>Register a GHG project via BEE methodologies and earn offset CCCs</p>
                  </div>
                  <p className={`font-sans text-xs italic ${mechTab==='offset' ? 'text-white/50' : 'text-brand-brown'}`}>Offset CCCs cannot cover a compliance shortfall</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Enforcement footnote */}
          <div className="border-t border-black/10 pt-4 mt-8 flex items-start gap-3">
            <Shield className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="font-sans text-sm text-gray-500"><strong className="text-brand-dark">Enforcement:</strong> CPCB order = 2x average CCC price, due 90 days, further penalty under EP Act if unpaid.</p>
          </div>
        </div>
      </section>

      {/* ═══════════════ COMPLIANCE CYCLE ═══════════════ */}
      <section className="py-10 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mb-2">The Compliance <span className="bg-gradient-to-r from-brand-brown to-brand-green bg-clip-text text-transparent">Journey</span></h2>
          <p className="font-sans text-base text-gray-500 mb-8">From action plan to trading — every step mapped out.</p>
          <ComplianceCycleSection />
        </div>
      </section>

      {/* ═══════════════ CURRICULUM ═══════════════ */}
      <section className="py-10 border-t border-gray-100" data-testid="curriculum-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mb-1">The Full <span className="bg-gradient-to-r from-brand-green to-brand-brown bg-clip-text text-transparent">Curriculum</span></h2>
          <p className="font-sans text-base text-gray-500 mb-6">Hour by hour — this is what you&apos;re investing in.</p>

          <div className="flex items-center gap-6 mb-6">
            <div className="flex gap-2">
              {['day1','day2'].map(t => (
                <button key={t} onClick={() => setCurrTab(t)} className={`font-sans text-sm px-5 py-2 rounded-full border transition-all ${currTab===t ? 'bg-brand-green text-white border-brand-green' : 'border-gray-200 text-gray-500 hover:border-brand-green'}`}>
                  {t==='day1' ? 'Day 1 · Foundations · 180 min' : 'Day 2 · Mechanics · 165 min'}
                </button>
              ))}
            </div>
            <div className="hidden md:flex gap-6 ml-auto">
              {[['9','Modules'],['6','Hours'],['2','Days']].map(([n,l]) => (
                <div key={l} className="text-center">
                  <span className="font-serif text-3xl text-brand-green font-bold">{n}</span>
                  <span className="font-mono text-xs text-gray-400 uppercase ml-1">{l}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            {(currTab==='day1' ? DAY1_MODULES : DAY2_MODULES).map(m => (
              <Accordion key={m.id} title={`${m.id} ${m.title}`} time={m.time} badge={m.badge} accent={currTab==='day1'?'green':'brown'}>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 pt-1">
                  {m.items.map((item,j) => (
                    <div key={j} className="flex items-start gap-2">
                      <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 opacity-60 ${currTab==='day1' ? 'text-brand-green' : 'text-brand-brown'}`} />
                      <span className="font-sans text-base text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>
              </Accordion>
            ))}
          </div>
          <p className="font-sans text-center text-base text-gray-500 mt-6 italic">Two days, nine modules, no fluff — you walk out with a working CCC strategy, not just notes.</p>
        </div>
      </section>

      {/* ═══════════════ OUTCOMES ═══════════════ */}
      <section className="py-10 border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mb-6">What You&apos;ll <span className="bg-gradient-to-r from-brand-brown to-brand-green bg-clip-text text-transparent">Walk Away With</span></h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {OUTCOMES.map((o,i) => (
              <div key={`outcome-${i}`} className="flex items-start gap-3 border border-gray-200 rounded-xl p-4">
                <CheckCircle2 className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
                <span className="font-sans text-base text-gray-700">{o}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ SPEAKERS ═══════════════ */}
      <section className="py-10 border-t border-gray-100" data-testid="speakers-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <p className="font-mono text-xs text-gray-400 uppercase tracking-[0.15em]">Why Maple</p>
          <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mt-2 mb-8">Meet the <span className="italic bg-gradient-to-r from-brand-green to-brand-brown bg-clip-text text-transparent">Experts</span></h2>
          <div className="grid md:grid-cols-2 gap-6">
            {SPEAKERS.map((s,i) => (
              <div key={s.name} className="border border-gray-200 rounded-2xl overflow-hidden" data-testid={`speaker-card-${i}`}>
                <div className={`h-1.5 ${s.accent==='green' ? 'bg-brand-green' : 'bg-brand-brown'}`} />
                <div className="p-6">
                  {s.photo ? (
                    <img
                      src={s.photo}
                      alt={s.name}
                      className="w-20 h-20 rounded-full object-cover mb-4 border-2 border-gray-100"
                    />
                  ) : (
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 font-serif text-2xl text-white ${s.accent==='green' ? 'bg-brand-green' : 'bg-brand-brown'}`}>{s.initials}</div>
                  )}
                  <h3 className="font-serif text-xl text-brand-dark">{s.name}</h3>
                  <div className="flex items-center gap-2 mb-3">
                    <p className={`font-sans text-sm ${s.accent==='green' ? 'text-brand-green' : 'text-brand-brown'}`}>{s.role}</p>
                    {s.linkedin && <a href={s.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#0077b5] transition-colors"><Linkedin className="w-4 h-4" /></a>}
                  </div>
                  <p className="font-sans text-sm text-gray-500 leading-relaxed">{s.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ PRICING ═══════════════ */}
      <section className="py-10 border-t border-gray-100" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="border border-gray-200 rounded-3xl p-8 text-center">
            <div className="flex justify-center gap-3 mb-3">
              <span className="font-mono text-xs text-brand-brown uppercase tracking-widest bg-brand-brown/10 px-3 py-1 rounded-full">Early-Bird · Ends 1 Sep</span>
              <span className="font-mono text-xs bg-brand-green text-white px-3 py-1 rounded-full uppercase">Save 40%</span>
            </div>
            <div className="my-6">
              <div className="border border-brand-green/20 rounded-2xl p-8 max-w-sm mx-auto">
                <p className="font-serif text-5xl text-brand-dark font-bold">₹2,999</p>
                <p className="font-sans text-sm text-gray-400 line-through mt-1">₹4,999</p>
                <p className="font-sans text-sm text-gray-500 mt-2">Early-bird price per participant</p>
              </div>
            </div>
            <div className="flex justify-center mb-4"><Countdown /></div>
            <div className="flex flex-wrap justify-center gap-2 mb-5">
              {['12 & 13 Sep','3 hrs each','Zoom','Certificate + slides + templates'].map(t => (
                <span key={t} className="font-sans text-sm text-gray-500 bg-gray-50 border border-gray-100 px-3 py-1 rounded-full">{t}</span>
              ))}
            </div>
            <RazorpayButton />
            <p className="font-sans text-sm text-gray-400 mt-4">Prices rise after 1 September. Group discounts on request.</p>
            <div className="flex items-center justify-center gap-6 mt-2 text-sm text-gray-400">
              <span>Contact: Shreea Singh</span>
              <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> +91 99716 99133</span>
              <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> trainings@mapleintegratedsolutions.com</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ FAQ ═══════════════ */}
      <section className="py-10 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mb-6">Common Questions</h2>
          <div className="space-y-2">
            {FAQ_ITEMS.map((f,i) => (
              <Accordion key={f.q} title={f.q} time="">
                <p className="font-sans text-base text-gray-600">{f.a}</p>
              </Accordion>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default CCTSTrainingPage;
